import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useHistory, useLocation } from "react-router-dom";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { GET_THEORETICAL_VS_ACTUAL, GET_BRANCHES } from "./queries";
import { TheoreticalVsActualItem } from "./models";
import InventoryNav from "./inventoryNav";

const TheoreticalVsActual: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .split("T")[0];
  const today = now.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(
    params.get("startDate") || firstDay
  );
  const [endDate, setEndDate] = useState(params.get("endDate") || today);
  const [branchFilter, setBranchFilter] = useState(
    params.get("branch_id") || ""
  );

  const [fetchReport, { data, loading }] = useLazyQuery(
    GET_THEORETICAL_VS_ACTUAL,
    { fetchPolicy: "no-cache" }
  );
  const [fetchBranches, { data: branchData }] = useLazyQuery(GET_BRANCHES, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    fetchBranches();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchReport({
      variables: {
        startDate,
        endDate,
        branch_id: branchFilter ? parseInt(branchFilter) : undefined,
      },
    });
    //eslint-disable-next-line
  }, [startDate, endDate, branchFilter]);

  const report = data?.getTheoreticalVsActualReport;
  const items: TheoreticalVsActualItem[] = report?.items || [];

  const getVarianceClass = (variance: number, variancePct: number | null) => {
    if (variancePct === null) return "";
    const abs = Math.abs(variancePct);
    if (abs <= 5) return "text-success";
    if (abs <= 15) return "text-warning font-weight-bold";
    return "text-danger font-weight-bold";
  };

  return (
    <>
      <div className="d-flex align-items-center mb-4">
        <button
          className="btn btn-light mr-3"
          onClick={() => history.push("/inventory/food-cost")}
        >
          <i className="fa fa-arrow-left" />
        </button>
        <h4 className="mb-0 font-weight-bold">
          {lang("Consumo Teórico vs Real", "Theoretical vs Actual Consumption")}
        </h4>
      </div>
      <InventoryNav active="food-cost" />

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-lg-2">
          <label className="small text-muted mb-1">
            {lang("Desde", "From")}
          </label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="col-lg-2">
          <label className="small text-muted mb-1">
            {lang("Hasta", "To")}
          </label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="col-lg-2">
          <label className="small text-muted mb-1">
            {lang("Sucursal", "Branch")}
          </label>
          <select
            className="custom-select custom-select-sm"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
          >
            <option value="">{lang("Todas", "All")}</option>
            {branchData?.getBranches?.map((b: any) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <LoaderDots fullSize />}

      {report && (
        <>
          {/* Summary */}
          <div className="row mb-3">
            <div className="col-lg-4">
              <div className="material_card p-3 text-center">
                <small className="text-muted">
                  {lang("Ingredientes Analizados", "Ingredients Analyzed")}
                </small>
                <h3>{items.length}</h3>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="material_card p-3 text-center">
                <small className="text-muted">
                  {lang("Con varianza > 10%", "Variance > 10%")}
                </small>
                <h3 className="text-warning">
                  {
                    items.filter(
                      (i) =>
                        i.variance_pct !== null &&
                        Math.abs(i.variance_pct) > 10
                    ).length
                  }
                </h3>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="material_card p-3 text-center">
                <small className="text-muted">
                  {lang("Con varianza > 25%", "Variance > 25%")}
                </small>
                <h3 className="text-danger">
                  {
                    items.filter(
                      (i) =>
                        i.variance_pct !== null &&
                        Math.abs(i.variance_pct) > 25
                    ).length
                  }
                </h3>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="row">
            <div className="col-lg-12">
              <div className="material_card p-3 table-responsive">
                <table className="table vertical_middle">
                  <thead className="thead-light">
                    <tr>
                      <th>{lang("Ingrediente", "Ingredient")}</th>
                      <th>{lang("Unidad", "Unit")}</th>
                      <th className="text-right">
                        {lang("Teórico", "Theoretical")}
                      </th>
                      <th className="text-right">
                        {lang("Real", "Actual")}
                      </th>
                      <th className="text-right">
                        {lang("Varianza", "Variance")}
                      </th>
                      <th className="text-right">
                        {lang("Varianza %", "Variance %")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={item.ingredient?.id || idx}>
                        <td className="font-weight-bold">
                          {item.ingredient?.name || "—"}
                        </td>
                        <td className="text-muted">
                          {item.unit || item.ingredient?.unit || "—"}
                        </td>
                        <td className="text-right">
                          {Number(item.theoretical_qty).toLocaleString(
                            "es-MX",
                            { maximumFractionDigits: 2 }
                          )}
                        </td>
                        <td className="text-right">
                          {Number(item.actual_qty).toLocaleString("es-MX", {
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td
                          className={`text-right ${getVarianceClass(
                            item.variance,
                            item.variance_pct
                          )}`}
                        >
                          {item.variance > 0 ? "+" : ""}
                          {Number(item.variance).toLocaleString("es-MX", {
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td
                          className={`text-right ${getVarianceClass(
                            item.variance,
                            item.variance_pct
                          )}`}
                        >
                          {item.variance_pct !== null
                            ? `${item.variance_pct > 0 ? "+" : ""}${item.variance_pct.toFixed(
                                1
                              )}%`
                            : "—"}
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          {lang(
                            "No hay datos para este período",
                            "No data for this period"
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <small className="text-muted">
                  {lang(
                    "Varianza positiva = se consumió más de lo esperado. Negativa = se consumió menos.",
                    "Positive variance = consumed more than expected. Negative = consumed less."
                  )}
                </small>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default TheoreticalVsActual;
